import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import type { EventParticipantCandidateDto } from "@/api/types";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import { Card } from "@/design-system/Card";
import { CollectionEmptyState, CollectionLoadingState } from "@/design-system/CollectionState";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { useDebounceValue } from "@/hooks/useDebounce";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { useEventQueries } from "@/queries/events/queryOptions";
import { useTranslation } from "@/translations";

type AddEventParticipantModalProps = ModalBaseProps & {
  eventId: string;
  isAdding: boolean;
  onSelect: (candidate: EventParticipantCandidateDto) => void;
};

export function AddEventParticipantModal({
  eventId,
  isOpened,
  isAdding,
  onOpenChange,
  onSelect,
}: AddEventParticipantModalProps): React.ReactNode {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounceValue(search.trim(), 300);
  const eventQueries = useEventQueries();
  const candidatesQuery = useInfiniteQuery({
    ...eventQueries.participantCandidatesInfinite(eventId, debouncedSearch),
    enabled: isOpened,
  });
  const candidates = candidatesQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const loadMoreRef = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (candidatesQuery.hasNextPage && !candidatesQuery.isFetchingNextPage) {
        void candidatesQuery.fetchNextPage();
      }
    }, [candidatesQuery]),
  });
  let content: React.ReactNode;
  if (candidatesQuery.isLoading) {
    content = <CollectionLoadingState />;
  } else if (candidates.length === 0) {
    content = (
      <CollectionEmptyState presentation="compact">{t("page.event.participants.add.no-results")}</CollectionEmptyState>
    );
  } else {
    content = (
      <Stack gap="sm">
        {candidates.map((candidate) => (
          <Card
            as="button"
            type="button"
            density="compact"
            interactive
            key={candidate.id}
            onClick={() => onSelect(candidate)}
            data-testid={`event-participant-candidate-${candidate.id}`}
          >
            <Inline gap="sm" align="center" wrap={false}>
              <UserAvatar image={candidate.avatar} />
              <span className="flex min-w-0 flex-1 flex-col text-left">
                <span className="truncate text-body-emphasis">{candidate.fullName}</span>
                <span className="truncate text-caption text-text-secondary">{formatCandidateSubtitle(candidate)}</span>
              </span>
            </Inline>
          </Card>
        ))}
        {candidatesQuery.hasNextPage && (
          <CollectionLoadingState ref={loadMoreRef} show={candidatesQuery.isFetchingNextPage} />
        )}
      </Stack>
    );
  }

  return (
    <Modal.Root
      title={t("page.event.participants.add.title")}
      description={t("page.event.participants.add.description")}
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      isPending={isAdding}
      data-testid="add-event-participant-modal"
    >
      <Modal.Body>
        <Stack gap="md">
          <SearchInput
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("page.event.participants.add.search-placeholder")}
            data-testid="event-participant-candidate-search"
          />
          <div className="max-h-scroll-area-compact overflow-y-auto">{content}</div>
        </Stack>
      </Modal.Body>
    </Modal.Root>
  );
}

function formatCandidateSubtitle(candidate: EventParticipantCandidateDto): string {
  const address = [candidate.address.street, candidate.address.houseNumber, candidate.address.houseNumberSuffix]
    .filter(Boolean)
    .join(" ");

  return [address, candidate.project.name].filter(Boolean).join(" · ");
}
